import React from "react";
import useGetSendMoneyCountries from "../../hooks/sendMoney/useGetSendMoneyCountries";
import {
  useCountryCurrencyForm,
  COUNTRY_FIELD,
} from "../../hooks/sendMoney/useCountryCurrencyForm";
import DynamicField from "../../components/DynamicForms/DynamicField";
import i18n from "../../i18n/config";
import { ICONS } from "../../constants/enums";

// ui components
import FlowScreen from "../../components/Layouts/FlowScreen";
import SingleColumnLayout from "../../components/Layouts/SingleColumnLayout";
import Alert from "../../components/Common/Alerts/Alert";
import SpinningLoader from "../../components/Common/SpinningLoader";
import RenderCountryOption from "../../components/DynamicForms/RenderCountryOption";
import Action from "../../components/Common/Action";
import RenderCurrencyOption from "../../components/DynamicForms/RenderCurrencyOption";
import ApiErrorModal from "../../components/Common/Modals/ApiErrorModal";

const SendMoneyCountryCurrency = (): React.ReactElement => {
  const { loading, countries, countryOptions, banners, hasError } =
    useGetSendMoneyCountries();

  const { form, renderFields, submit, goBack, selectedCountryIso } = useCountryCurrencyForm({
    countries,
    countryOptions,
  });
  const { control, formState } = form;

  if (loading) return <SpinningLoader show={loading} />;

  const renderBody = () => {
    if (hasError) {
      return (
        <>
          <Alert
            alertClasses={`alert-info flex-row text-primary gap-8 leading-[160%] font-size-lg`}
            message={i18n.t("PAGES.SEND_MONEY.COUNTRY_AND_CURRENCY.ERROR_ALERT_MESSAGE")}
            textClasses="font-size-lg"
            icon={ICONS.WARNING_INFO}
          />
          <Action onClick={goBack} title={i18n.t("COMMON.GO_BACK")} />
        </>
      );
    }
    if (countryOptions.length === 0) {
      return (
        <Alert
          alertClasses="alert-info flex-row gap-8 leading-[160%] font-size-lg"
          textClasses="font-size-lg"
          message={i18n.t("PAGES.SEND_MONEY.COUNTRY_AND_CURRENCY.NO_CORRIDORS_ALERT_MESSAGE")}
          icon={ICONS.INFO}
        />
      );
    }
    return (
      <>
        {banners.map((b, i) => (
          <Alert
            key={i}
            alertClasses={`alert-info flex-row text-primary gap-8 leading-[160%]`}
            textClasses="font-size-lg"
            message={b.message}
            icon={ICONS.INFO}
          />
        ))}
        {renderFields.map((field) => (
          <DynamicField
            key={field.name} field={field}
            control={control}
            formatOptionLabel={
              field.name === COUNTRY_FIELD
                ? RenderCountryOption
                : RenderCurrencyOption(selectedCountryIso)
            }
          />
        ))}
        <Action onClick={submit} disabled={!formState.isValid} type="button" title={i18n.t("COMMON.CONTINUE")} />
      </>
    );
  };

  return (
    <FlowScreen
      flowTitle={i18n.t("PAGES.SEND_MONEY.COUNTRY_AND_CURRENCY.TITLE")}
      showBackButton
      customBackButtonFunc={() => goBack()}
      className="v3"
    >
      <SingleColumnLayout>
        {renderBody()}
      </SingleColumnLayout>

      <ApiErrorModal />
    </FlowScreen>
  );
};

export default SendMoneyCountryCurrency;